import { api_fetch_throws } from "./client"

// Types

export interface WizardConversation {
  id: string
  org_id: string
  user_id: string
  title: string | null
  created_at: string
  updated_at: string
}

export interface WizardMessage {
  id: string
  conversation_id: string
  role: "user" | "assistant" | "tool"
  content: string
  tool_calls: any | null
  tool_results: any | null
  metadata: any | null
  created_at: string
}

export interface ConfirmationAction {
  action_type: string
  label: string
  params: any
}

// SSE Event Types

export interface WizardThinkingEvent {
  type: "thinking"
  message: string
}

export interface WizardThinkingDeltaEvent {
  type: "thinking_delta"
  content: string
}

export interface WizardThinkingDoneEvent {
  type: "thinking_done"
}

export interface WizardToolCallEvent {
  type: "tool_call"
  tool_name: string
  args: any
}

export interface WizardToolResultEvent {
  type: "tool_result"
  tool_name: string
  result: any
  confirmation_required: boolean
  confirmation_action?: ConfirmationAction
}

export interface WizardTextDeltaEvent {
  type: "text_delta"
  content: string
}

export interface WizardTextEvent {
  type: "text"
  content: string
}

export interface WizardDoneEvent {
  type: "done"
  cost?: { total_cost_usd: number }
}

export interface WizardErrorEvent {
  type: "error"
  error: string
}

export type WizardStreamEvent =
  | WizardThinkingEvent
  | WizardThinkingDeltaEvent
  | WizardThinkingDoneEvent
  | WizardToolCallEvent
  | WizardToolResultEvent
  | WizardTextDeltaEvent
  | WizardTextEvent
  | WizardDoneEvent
  | WizardErrorEvent

export interface WizardStreamCallbacks {
  onThinking: (message: string) => void
  onThinkingDelta: (content: string) => void
  onThinkingDone: () => void
  onToolCall: (toolName: string, args: any) => void
  onToolResult: (toolName: string, result: any, confirmationRequired: boolean, confirmationAction?: ConfirmationAction) => void
  onTextDelta: (content: string) => void
  onText: (content: string) => void
  onDone: () => void
  onError: (error: string) => void
}

// API Functions

export async function createConversation(title?: string): Promise<WizardConversation> {
  return api_fetch_throws<WizardConversation>("/wizard/conversations", {
    method: "POST",
    body: { title },
  })
}

export async function listConversations(): Promise<WizardConversation[]> {
  return api_fetch_throws<WizardConversation[]>("/wizard/conversations")
}

export async function getConversation(id: string): Promise<WizardConversation> {
  return api_fetch_throws<WizardConversation>(`/wizard/conversations/${id}`)
}

export async function getMessages(conversationId: string): Promise<WizardMessage[]> {
  return api_fetch_throws<WizardMessage[]>(`/wizard/conversations/${conversationId}/messages`)
}

export async function confirmAction(conversationId: string, actionType: string, params: any): Promise<any> {
  return api_fetch_throws<any>(`/wizard/conversations/${conversationId}/confirm`, {
    method: "POST",
    body: { action_type: actionType, params },
  })
}

/** Send a chat message and stream the response via SSE */
export function sendMessageStream(
  conversationId: string,
  content: string,
  callbacks: WizardStreamCallbacks,
  options?: { deductionId?: string; files?: File[] },
): AbortController {
  const abortController = new AbortController()

  // Always use FormData so the server can accept files alongside text
  const formData = new FormData()
  formData.append("content", content)
  if (options?.deductionId) {
    formData.append("deduction_id", options.deductionId)
  }
  if (options?.files) {
    for (const file of options.files) {
      formData.append("files", file)
    }
  }

  fetch(`/api/wizard/conversations/${conversationId}/chat/stream`, {
    method: "POST",
    headers: { Accept: "text/event-stream" },
    credentials: "same-origin",
    body: formData,
    signal: abortController.signal,
  })
    .then(async (response: Response) => {
      if (!response.ok) {
        const text = await response.text()
        callbacks.onError(`Request failed: ${text}`)
        return
      }

      const reader = response.body?.getReader()
      if (!reader) {
        callbacks.onError("No response body")
        return
      }

      const decoder = new TextDecoder()
      let buffer = ""
      let doneReceived = false

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split("\n")
        buffer = lines.pop() || ""

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6).trim()
            if (!data) continue
            try {
              const event: WizardStreamEvent = JSON.parse(data)
              switch (event.type) {
                case "thinking":
                  callbacks.onThinking(event.message)
                  break
                case "thinking_delta":
                  callbacks.onThinkingDelta(event.content)
                  break
                case "thinking_done":
                  callbacks.onThinkingDone()
                  break
                case "tool_call":
                  callbacks.onToolCall(event.tool_name, event.args)
                  break
                case "tool_result":
                  callbacks.onToolResult(event.tool_name, event.result, event.confirmation_required, event.confirmation_action)
                  break
                case "text_delta":
                  callbacks.onTextDelta(event.content)
                  break
                case "text":
                  callbacks.onText(event.content)
                  break
                case "done":
                  doneReceived = true
                  callbacks.onDone()
                  break
                case "error":
                  callbacks.onError(event.error)
                  break
              }
            } catch {
              // Skip malformed JSON
            }
          }
        }
      }

      // Only call onDone as a fallback if the server never sent a done event
      if (!doneReceived) {
        callbacks.onDone()
      }
    })
    .catch((err: Error) => {
      if (err.name !== "AbortError") {
        callbacks.onError(err.message)
      }
    })

  return abortController
}
